/**
 * 2 Note Fragrance Finder Quiz
 * Luxury botanical fragrance recommendation quiz
 */

(function () {
  "use strict";

  // Guard against double-execution: the section self-loads this script and
  // layout/theme.liquid may also load it on the finder page. Each <script>
  // runs the IIFE in its own scope, so the module-scoped `initialized` flag
  // below cannot prevent a second execution — a window-level flag can.
  if (window.__fragranceFinderLoaded) return;
  window.__fragranceFinderLoaded = true;

  // Configuration
  const STORAGE_KEY = "fragranceQuiz";
  const RESULTS_STORAGE_KEY = "fragranceQuizResults";
  const STORAGE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours

  // Archetype definitions
  const ARCHETYPES = {
    A: {
      name: "The Jazz Classic",
      description: "Refined · Understated · Confident",
      compositions: ["nocturne", "legato"],
    },
    B: {
      name: "The Folk Spirit",
      description: "Warm · Grounded · Easygoing",
      compositions: ["sonata", "rhapsody"],
    },
    C: {
      name: "The Pop Virtuoso",
      description: "Bright · Energetic · Expressive",
      compositions: ["vivace", "sonata"],
    },
    D: {
      name: "The Ballad Heart",
      description: "Soft · Romantic · Open",
      compositions: ["encore", "rhapsody"],
    },
    E: {
      name: "The Minimal Muse",
      description: "Calm · Serene · Composed",
      compositions: ["legato", "nocturne"],
    },
  };

  const DEFAULT_ARCHETYPE_KEY = "A";

  // Layering data mapping (from data/layering-recommendations-review.json)
  const LAYERING_MAP = {
    encore: ["rose-otto-chord", "patchouli-chord"],
    legato: ["bergamot-chord", "patchouli-chord"],
    nocturne: ["rose-otto-chord", "vetiver-chord"],
    rhapsody: ["rose-otto-chord", "cedarwood-chord"],
    sonata: ["sandalwood-chord", "patchouli-chord"],
    vivace: ["vetiver-chord", "bergamot-chord"],
  };

  // State
  let quizState = {
    answers: [],
    currentStep: 0,
    email: null,
    timestamp: null,
    highestQuestionReached: 1, // Track furthest question for next button logic
  };

  // DOM Elements
  let elements = {};

  // Initialization guard
  let initialized = false;

  /**
   * Initialize the quiz
   */
  async function initQuiz() {
    // Prevent multiple initializations
    if (initialized) {
      return;
    }
    initialized = true;

    // Cache DOM elements
    elements = {
      container: document.querySelector("[data-quiz-container]"),
      hero: document.querySelector("[data-quiz-hero]"),
      startButton: document.querySelector("[data-quiz-start]"),
      progress: document.querySelector("[data-quiz-progress]"),
      progressText: document.querySelector("[data-progress-text]"),
      questionsContainer: document.querySelector("[data-quiz-questions]"),
      questions: document.querySelectorAll("[data-question]"),
      emailCapture: document.querySelector("[data-email-capture]"),
      emailForm: document.querySelector("[data-email-form]"),
      skipEmail: document.querySelector("[data-skip-email]"),
      results: document.querySelector("[data-quiz-results]"),
      archetypeName: document.querySelector("[data-archetype-name]"),
      archetypeDescription: document.querySelector("[data-archetype-description]"),
      primaryProducts: document.querySelector("[data-primary-products]"),
      chordProducts: document.querySelector("[data-chord-products]"),
      restartButton: document.querySelector("[data-restart-quiz]"),
    };

    // Attach restart button listener (needed for saved results)
    if (elements.restartButton) {
      elements.restartButton.addEventListener("click", restartQuiz);
    }

    // Check for saved results first
    const savedResults = checkSavedResults();
    if (savedResults) {
      // Automatically show saved results
      renderSavedResults(savedResults);
      return; // Don't proceed with normal quiz flow
    }

    // Check for saved progress
    checkSavedProgress();

    // Attach event listeners
    if (elements.startButton) {
      elements.startButton.addEventListener("click", startQuiz);
    }

    if (elements.skipEmail) {
      elements.skipEmail.addEventListener("click", skipEmailCapture);
    }

    // Attach email back button listener
    const emailBackButton = document.querySelector("[data-email-back]");
    if (emailBackButton) {
      emailBackButton.addEventListener("click", handleEmailBack);
    }

    if (elements.emailForm) {
      elements.emailForm.addEventListener("submit", handleEmailSubmit);
    }

    // Attach answer button listeners
    attachAnswerListeners();

    // Attach back button listeners
    const backButtons = document.querySelectorAll("[data-quiz-back]");
    backButtons.forEach((button) => {
      button.addEventListener("click", handleBack);
    });

    // Attach next button listeners
    const nextButtons = document.querySelectorAll("[data-quiz-next]");
    nextButtons.forEach((button) => {
      button.addEventListener("click", handleNext);
    });
  }

  /**
   * Check for saved quiz progress in localStorage
   */
  function checkSavedProgress() {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) return;

      const data = JSON.parse(saved);
      const now = Date.now();

      // Check if data is expired
      if (data.timestamp && now - data.timestamp > STORAGE_EXPIRY) {
        localStorage.removeItem(STORAGE_KEY);
        return;
      }

      // Restore state
      if (data.answers && data.answers.length > 0) {
        // Show custom styled restore prompt
        showSavedProgressPopup(data);
      }
    } catch (error) {
      console.error("Error restoring quiz progress:", error);
      localStorage.removeItem(STORAGE_KEY);
    }
  }

  /**
   * Show custom saved progress popup
   */
  function showSavedProgressPopup(data) {
    const popup = document.createElement("div");
    popup.className = "quiz-saved-progress-popup";
    popup.innerHTML = `
      <div class="quiz-saved-progress-popup__content">
        <h2 class="quiz-saved-progress-popup__title">Welcome Back</h2>
        <p class="quiz-saved-progress-popup__message">Would you like to continue where you left off?</p>
        <div class="quiz-saved-progress-popup__actions">
          <button class="quiz-saved-progress-popup__button quiz-saved-progress-popup__button--primary" data-continue>
            Continue
          </button>
          <button class="quiz-saved-progress-popup__button quiz-saved-progress-popup__button--secondary" data-start-fresh>
            Start Fresh
          </button>
        </div>
      </div>
    `;

    document.body.appendChild(popup);

    // Continue button
    popup.querySelector("[data-continue]").addEventListener("click", () => {
      quizState = data;
      resumeQuiz();
      document.body.removeChild(popup);
    });

    // Start fresh button
    popup.querySelector("[data-start-fresh]").addEventListener("click", () => {
      localStorage.removeItem(STORAGE_KEY);
      document.body.removeChild(popup);
    });
  }

  /**
   * Save quiz progress to localStorage
   */
  function saveProgress() {
    try {
      quizState.timestamp = Date.now();
      localStorage.setItem(STORAGE_KEY, JSON.stringify(quizState));
    } catch (error) {
      console.error("Error saving quiz progress:", error);
    }
  }

  /**
   * Resume quiz from saved state
   */
  function resumeQuiz() {
    // Hide hero
    elements.hero.hidden = true;

    // Show progress
    elements.progress.hidden = false;
    elements.questionsContainer.hidden = false;

    // Show current question
    showQuestion(quizState.currentStep + 1);
  }

  /**
   * Start the quiz
   */
  function startQuiz() {
    // Reset state
    quizState = {
      answers: [],
      currentStep: 0,
      email: null,
      timestamp: Date.now(),
      highestQuestionReached: 1,
    };

    // Hide hero
    elements.hero.hidden = true;

    // Show progress and first question
    elements.progress.hidden = false;
    elements.questionsContainer.hidden = false;

    showQuestion(1);
    saveProgress();
  }

  /**
   * Show a specific question
   */
  function showQuestion(step) {
    console.log("[DEBUG] showQuestion called with step:", step);

    // Hide all questions
    elements.questions.forEach((q) => {
      q.hidden = true;
    });

    // Show current question
    const currentQuestion = document.querySelector(`[data-question="${step}"]`);
    console.log("[DEBUG] currentQuestion found:", !!currentQuestion);

    if (currentQuestion) {
      currentQuestion.hidden = false;

      // Update progress text within this question
      const progressText = currentQuestion.querySelector("[data-progress-text]");
      if (progressText) {
        progressText.textContent = `${step} of 6`;
      }

      // Handle back button visibility
      const backButton = currentQuestion.querySelector("[data-quiz-back]");
      console.log("[DEBUG] backButton found:", !!backButton, "step:", step);
      if (backButton) {
        backButton.hidden = step === 1;
        console.log("[DEBUG] backButton.hidden set to:", backButton.hidden);
      }

      // Check if current question has an answer
      const previousAnswer = quizState.answers[step - 1];
      console.log("[DEBUG] previousAnswer:", previousAnswer);

      // Handle next button visibility - ONLY show if user has navigated backwards
      const nextButton = currentQuestion.querySelector("[data-quiz-next]");
      console.log("[DEBUG] nextButton found:", !!nextButton);
      const isNavigatingBackwards = step < quizState.highestQuestionReached;
      console.log(
        "[DEBUG] isNavigatingBackwards:",
        isNavigatingBackwards,
        "step:",
        step,
        "highest:",
        quizState.highestQuestionReached,
      );

      if (nextButton) {
        // Only show next button if:
        // 1. User has navigated backwards (step < highestQuestionReached)
        // 2. AND current question has an answer
        nextButton.hidden = !(isNavigatingBackwards && previousAnswer);
        console.log("[DEBUG] nextButton.hidden set to:", nextButton.hidden);
      }

      // Reset all answer buttons in this question
      const chips = currentQuestion.querySelectorAll("[data-answer]");
      chips.forEach((chip) => {
        chip.setAttribute("aria-pressed", "false");
        chip.classList.remove("active");
      });

      // Restore previous answer if it exists
      if (previousAnswer) {
        const previousChip = currentQuestion.querySelector(`[data-answer="${previousAnswer}"]`);
        if (previousChip) {
          previousChip.setAttribute("aria-pressed", "true");
          previousChip.classList.add("active");
        }
      }
    }

    quizState.currentStep = step - 1;
  }

  /**
   * Attach event listeners to answer buttons
   */
  function attachAnswerListeners() {
    const answerButtons = document.querySelectorAll("[data-answer]");
    answerButtons.forEach((button) => {
      button.addEventListener("click", handleAnswer);
    });
  }

  /**
   * Handle answer selection
   */
  function handleAnswer(event) {
    const button = event.currentTarget;
    const answer = button.getAttribute("data-answer");
    const question = button.closest("[data-question]");
    const questionNumber = parseInt(question.getAttribute("data-question"));

    console.log("[DEBUG] handleAnswer called, question:", questionNumber, "answer:", answer);

    // Update UI
    const allChips = question.querySelectorAll("[data-answer]");
    allChips.forEach((chip) => {
      chip.setAttribute("aria-pressed", "false");
      chip.classList.remove("active");
    });
    button.setAttribute("aria-pressed", "true");
    button.classList.add("active");

    // Save answer
    quizState.answers[questionNumber - 1] = answer;
    saveProgress();

    // Update highest question reached
    if (questionNumber >= quizState.highestQuestionReached) {
      quizState.highestQuestionReached = questionNumber + 1;
      console.log("[DEBUG] Updated highestQuestionReached to:", quizState.highestQuestionReached);
    }

    // If user is navigating backwards, show the next button
    const isNavigatingBackwards = questionNumber < quizState.highestQuestionReached - 1;
    console.log("[DEBUG] isNavigatingBackwards in handleAnswer:", isNavigatingBackwards);

    if (isNavigatingBackwards) {
      // Show next button for backwards navigation
      const nextButton = question.querySelector("[data-quiz-next]");
      if (nextButton) {
        nextButton.hidden = false;
        console.log("[DEBUG] Showing next button for backwards navigation");
      }
    }

    // AUTO-ADVANCE: Automatically move to next question after short delay
    // Only auto-advance if moving forward (not backwards navigation)
    if (!isNavigatingBackwards) {
      setTimeout(() => {
        if (questionNumber < 6) {
          showQuestion(questionNumber + 1);
        } else {
          // Quiz complete - show email capture or results
          if (elements.emailCapture) {
            showEmailCapture();
          } else {
            showResults();
          }
        }
      }, 300);
    }
  }

  /**
   * Handle back button click
   */
  function handleBack() {
    const currentStep = quizState.currentStep + 1;
    if (currentStep > 1) {
      showQuestion(currentStep - 1);
    }
  }

  /**
   * Handle next button click
   */
  function handleNext() {
    const currentStep = quizState.currentStep + 1;
    const currentAnswer = quizState.answers[currentStep - 1];

    // Only proceed if current question is answered
    if (!currentAnswer) {
      return;
    }

    if (currentStep < 6) {
      showQuestion(currentStep + 1);
    } else {
      // Quiz complete - show email capture or results
      if (elements.emailCapture) {
        showEmailCapture();
      } else {
        showResults();
      }
    }
  }

  /**
   * Show email capture screen
   */
  function showEmailCapture() {
    elements.questionsContainer.hidden = true;
    elements.progress.hidden = true;
    elements.emailCapture.hidden = false;
  }

  /**
   * Handle email form submission
   */
  function handleEmailSubmit(event) {
    // Don't prevent default - let Shopify handle the form submission
    // But first, save the quiz results so we can show them after page reload

    // If quizState.answers is empty, try to restore from localStorage
    if (!quizState.answers || quizState.answers.length === 0) {
      try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
          const data = JSON.parse(saved);
          if (data.answers && data.answers.length > 0) {
            quizState.answers = data.answers;
          }
        }
      } catch (e) {
        console.error("Error restoring answers:", e);
      }
    }

    const archetypeKey = calculateArchetype();

    try {
      sessionStorage.setItem(
        RESULTS_STORAGE_KEY,
        JSON.stringify({
          archetypeKey: archetypeKey,
          timestamp: Date.now(),
        }),
      );
    } catch (error) {
      console.error("Error saving results before email submission:", error);
    }

    // Form will submit naturally and page will reload
    // On reload, saved results will be automatically displayed
  }

  /**
   * Skip email capture
   */
  function skipEmailCapture() {
    showResults();
  }

  /**
   * Go back from email capture to last question
   */
  function handleEmailBack() {
    elements.emailCapture.hidden = true;
    showQuestion(6); // Go back to question 6 (last question)
  }

  /**
   * Calculate archetype from answers
   */
  function calculateArchetype() {
    // Count occurrences of each answer
    const counts = { A: 0, B: 0, C: 0, D: 0, E: 0 };
    quizState.answers.forEach((answer) => {
      if (counts.hasOwnProperty(answer)) {
        counts[answer]++;
      }
    });

    // Find the most common answer(s)
    const maxCount = Math.max(...Object.values(counts));
    const topAnswers = Object.keys(counts).filter((key) => counts[key] === maxCount);

    // If tie, use tiebreaker logic (Q3 and Q5)
    if (topAnswers.length > 1) {
      // Check Q3 (The Lead Note - index 2)
      if (quizState.answers[2] && topAnswers.includes(quizState.answers[2])) {
        return quizState.answers[2];
      }

      // Check Q5 (Your Tempo - index 4)
      if (quizState.answers[4] && topAnswers.includes(quizState.answers[4])) {
        return quizState.answers[4];
      }

      // If still tied, return first
      return topAnswers[0];
    }

    return topAnswers[0];
  }

  /**
   * Show quiz results
   */
  function showResults() {
    // Hide email capture if shown
    if (elements.emailCapture) {
      elements.emailCapture.hidden = true;
    }

    // Calculate archetype (fall back to default if invalid)
    const archetypeKey = calculateArchetype();
    let archetype = ARCHETYPES[archetypeKey];
    if (!archetype) {
      console.warn("Invalid archetype, using fallback:", archetypeKey);
      archetype = ARCHETYPES[DEFAULT_ARCHETYPE_KEY];
    }
    if (!archetype) {
      console.error("No archetype available");
      return;
    }

    // Save results to sessionStorage
    try {
      sessionStorage.setItem(
        RESULTS_STORAGE_KEY,
        JSON.stringify({
          archetypeKey: archetypeKey,
          timestamp: Date.now(),
        }),
      );
    } catch (error) {
      console.error("Error saving results:", error);
    }

    // Update archetype display
    elements.archetypeName.textContent = archetype.name;
    elements.archetypeDescription.textContent = archetype.description;

    // Reset any previously revealed cards, then reveal this archetype's
    returnCardsToPool();

    // Reveal primary composition products
    renderProducts(archetype.compositions, elements.primaryProducts);

    // Reveal one chord recommendation based on layering data
    const chordHandles = getChordRecommendations(archetype.compositions);
    renderProducts([chordHandles[0]], elements.chordProducts);

    // Show results
    elements.results.hidden = false;

    // Clear saved progress
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error("Error clearing quiz progress:", error);
    }
  }

  /**
   * Check for saved results in sessionStorage
   */
  function checkSavedResults() {
    try {
      const saved = sessionStorage.getItem(RESULTS_STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error("Error reading saved results:", error);
    }
    return null;
  }

  /**
   * Render saved results from sessionStorage
   */
  function renderSavedResults(savedData) {
    const archetypeKey = savedData.archetypeKey;
    const archetype = ARCHETYPES[archetypeKey];

    if (!archetype) {
      console.error("Invalid saved archetype:", archetypeKey);
      sessionStorage.removeItem(RESULTS_STORAGE_KEY);
      return;
    }

    // Hide hero and show results
    if (elements.hero) {
      elements.hero.hidden = true;
    }

    // Update archetype display
    elements.archetypeName.textContent = archetype.name;
    elements.archetypeDescription.textContent = archetype.description;

    // Reset any previously revealed cards, then reveal this archetype's
    returnCardsToPool();

    // Reveal primary composition products
    renderProducts(archetype.compositions, elements.primaryProducts);

    // Reveal one chord recommendation
    const chordHandles = getChordRecommendations(archetype.compositions);
    renderProducts([chordHandles[0]], elements.chordProducts);

    // Show results
    elements.results.hidden = false;
  }

  /**
   * Get chord recommendations based on compositions
   */
  function getChordRecommendations(compositionHandles) {
    const chordSet = new Set();

    compositionHandles.forEach((handle) => {
      const chords = LAYERING_MAP[handle];
      if (chords) {
        chords.forEach((chord) => chordSet.add(chord));
      }
    });

    return Array.from(chordSet);
  }

  /**
   * Move all revealed pool cards back into the hidden pool
   */
  function returnCardsToPool() {
    const pool = document.querySelector("[data-quiz-card-pool]");
    if (!pool) return;
    document
      .querySelectorAll(
        "[data-primary-products] [data-pool-card], [data-chord-products] [data-pool-card]",
      )
      .forEach((card) => pool.appendChild(card));
  }

  /**
   * Reveal the matching pre-rendered product cards into a results grid
   */
  function renderProducts(handles, container) {
    const pool = document.querySelector("[data-quiz-card-pool]");
    if (!container || !pool) return;
    handles.forEach((handle) => {
      if (!handle) return;
      const card = pool.querySelector(`[data-pool-card="${handle}"]`);
      if (card) {
        container.appendChild(card);
      }
    });
  }

  /**
   * Restart quiz
   */
  function restartQuiz() {
    // Return any revealed cards to the pool so a retake can reveal them again
    returnCardsToPool();

    // Clear storage
    try {
      localStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(RESULTS_STORAGE_KEY);
    } catch (error) {
      console.error("Error clearing quiz:", error);
    }

    // Reset state
    quizState = {
      answers: [],
      currentStep: 0,
      email: null,
      timestamp: null,
      highestQuestionReached: 1,
    };

    // Hide results
    elements.results.hidden = true;

    // Show hero
    elements.hero.hidden = false;
    elements.progress.hidden = true;
    elements.questionsContainer.hidden = true;
    if (elements.emailCapture) {
      elements.emailCapture.hidden = true;
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initQuiz);
  } else {
    initQuiz();
  }
})();

/**
 * Add to cart function (global scope for inline onclick)
 */
function fragranceQuizAddToCart(variantId) {
  // Shopify Ajax Cart API
  fetch("/cart/add.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      id: variantId,
      quantity: 1,
    }),
  })
    .then((response) => response.json())
    .then((data) => {
      // Show success message or update cart
      console.log("Added to cart:", data);

      // Trigger cart drawer if theme has one
      if (typeof window.theme !== "undefined" && window.theme.cart) {
        window.theme.cart.refresh();
      }

      // Update cart count
      if (window.theme && window.theme.updateCartCount) {
        window.theme.updateCartCount();
      }
    })
    .catch((error) => {
      console.error("Error adding to cart:", error);
      alert("Sorry, there was an error adding this item to your cart.");
    });
}
